import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { combineLatest, EMPTY, firstValueFrom, from, Observable, of, throwError } from 'rxjs';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { SelectItem } from 'primeng/api';
import {
  IOpsViewerMessagesService,
  IJobWorkbookConfigurationService,
  UserAuthorizationService,
  IApplicationContextService
} from '../../../services/services';
import {
  JobWorkbookConfiguration,

  WorkbookJobTypes,
  JobLinker
} from '../../../models/models';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { getWorkbookTypesSelection } from '../../../models/workbookJobTypes';
import { ICurrentUserService } from '../../../services/currentUserService/icurrentuser.service';
import { ConsumerJobClient, IVendorJobClient, VendorJobClient, VendorJob as VendorJob, WellInfo, FluidInfo, PerformanceSummary, FluidSummary, User, UserClient, CasingTubings, CasingTubingDetail, Device, DeviceClient, DeviceJobMapping, Company, CompanyNameViewModel, CompanyClient } from '../../../services/nswag/nswagclient';
import { NavigationTraceService } from '../../../services/navigation-trace.service';
import { TelemetryService } from '../../../services/telemetry.service';
import { IUserManagerService } from '../../../services/iUserManager.service';

@Component({
  selector: 'app-vendor-job',

  standalone: false,
  templateUrl: './vendor-job.component.html',
  styleUrls: ['./vendor-job.component.css']
})
export class VendorJobComponent {

  //#region Component Properties
  // UI State properties
  modelInvalid = false;
  invalidCompanyId = false;
  showModelInvalidDialog = false;
  isNew = false;
  saving = false;
  // Job Properties
  job$!: Observable<VendorJob>;
  jobToUpdate: VendorJob = this.createDefaultJob();
  // Company properties
  vendorCompanies$!: Observable<SelectItem[]>;
  consumerCompanies$!: Observable<SelectItem[]>;
  allCompanies$!: Observable<SelectItem[]>;
  serviceProviderOptions$!: Observable<SelectItem[]>;
  company$!: Observable<Company>;

  // Workbook properties
  jobTypes: SelectItem[] = [];
  jobWorkbookConfiguration: JobWorkbookConfiguration | null = null;
  jobTypeSet = false;

  // User properties
  user$!: Observable<User>;
  companyContext$!: Observable<CompanyNameViewModel | null>;

  // Device properties
  devices$!: Observable<Device[]>;
  usersCompanyDevices$!: Observable<Device[]>;
  additionalServiceProvidersDevices$!: Observable<Device[]>;

  companyContextDeviceMaps$!: Observable<DeviceJobMapping[]>;

  // Job Linker
  jobLinker: JobLinker | null = null;

  // Other UI properties

  securityToken$!: Observable<string>;

  showCompanyContactInfoDialog = false;
  showManageFiles = false;
  disabled = false;
  isUserServiceProvider = false;
  sandReportBeginTime?: Date;
  sandReportEndTime?: Date;
  dataSheetReportBeginTime?: Date;
  dataSheetReportEndTime?: Date;

  //#endregion

  //isUserServiceProvider: boolean;

  // Remove selectedDevice property since we're not using a dropdown anymore
  showDeviceDialog = false;

  // Change property name and default value
  showOnlyUnassigned = true;
  routeJobId = '';

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    @Inject('IVendorJobClient') private _jobService: IVendorJobClient,
    private _consumerJobClient: ConsumerJobClient,
    private _companyService: CompanyClient,
    private _jobWorkbookConfigurationService: IJobWorkbookConfigurationService,
    private _userAuthorizationService: UserAuthorizationService,
    private _deviceClient: DeviceClient,
    private _applicationContextService: IApplicationContextService,
    private _messageService: IOpsViewerMessagesService,
    private _titleService: Title,
    private _currentUserService: ICurrentUserService,
    private _vendorJobClient: VendorJobClient,
    private _navigationTrace: NavigationTraceService,
    private _telemetryService: TelemetryService,
    private _userManagerService: IUserManagerService,
  ) {
    this.consumerCompanies$ = this._companyService.getConsumerCompanyNames().pipe(
      map(c => c.map(c => { return { label: c.companyName, value: c.companyId } })),
      catchError(error => {
        this._messageService.errorNoUiMessage(error, 'Could not load consumer companies');
        return of([]);
      }),
      shareReplay(1)
    );
    this.vendorCompanies$ = this._companyService.getVendorCompanyNames().pipe(
      map(c => c.map(c => { return { label: c.companyName, value: c.companyId } })),
      catchError(error => {
        this._messageService.errorNoUiMessage(error, 'Could not load vendor companies');
        return of([]);
      }),
      shareReplay(1)
    );
    const vendorAndConsumerCompanies$ = combineLatest([this.vendorCompanies$, this.consumerCompanies$]);
    this.allCompanies$ = vendorAndConsumerCompanies$.pipe(map(([v, c]) => v.concat(c)), shareReplay(1));

    const job$ = this._route.params.pipe(switchMap(params => {
      const jobId = params['id'];
      this.routeJobId = jobId ?? '';
      if (!jobId || jobId.toUpperCase() === 'NEW') {
        this.isNew = true;
        return from(this.createNewJob());
      } else {
        return this._jobService.getVendorJobById(jobId).pipe(
          catchError(err => this.redirectToConsumerJobIfNeeded(jobId, err))
        );
      }
    }), shareReplay(1));




    this.jobTypes = getWorkbookTypesSelection();
    this.securityToken$ = this._currentUserService.getAccessToken();

    this.companyContext$ = this._applicationContextService.companyContextObserver;

    const jobAndVendorCompanies$ = combineLatest([job$, this.vendorCompanies$]);
    this.serviceProviderOptions$ = jobAndVendorCompanies$.pipe(map(([job, companies]) => this.filterNotCompany(companies, job.companyId)), shareReplay(1));  //we don't want to be able to set the service provider to the same as the jobs company  
    this.job$ = jobAndVendorCompanies$.pipe(
      map(([job, companies]) => {
        this.normalizeJob(job);
        if (job.serviceProviderIds) {
          job.serviceProviderIds = job.serviceProviderIds.filter(id => companies.some(c => c.value === id));
        }
        return job;
      })
    );

    //set model
    this.job$.subscribe(job => {
      this.jobToUpdate = this.normalizeJob(job);
      this.setPageTitle(job.id);
    });
      



    this.company$ = this.companyContext$.pipe(
      switchMap(c => c?.companyId ? this._companyService.getById(c.companyId) : of(new Company())),
      shareReplay(1)
    );

    this.user$ = this._userManagerService.getCurrentUser().pipe(shareReplay(1));
    this.devices$ = this.job$.pipe(
      switchMap(j => j.companyId ? this._deviceClient.getDevicesForCompany(j.companyId) : of([])),
      shareReplay(1)
    );

    this.usersCompanyDevices$ = combineLatest([this.companyContext$, this.job$]).pipe(switchMap(([c, j]) => {
      if (!c?.companyId) {
        return of([]);
      }
      if (c.companyId === j.companyId) {
        return this.devices$;
      }
      return this._deviceClient.getDevicesForCompany(c.companyId);
    }), map(d => d.sort((a, b) => (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase()))), shareReplay(1));

    this.additionalServiceProvidersDevices$ = combineLatest([this.companyContext$, this.job$]).pipe(switchMap(([c, j]) => {
      if (!j.serviceProviderIds || j.serviceProviderIds.length < 1) return of([]);
      const additionalCompanies = j.serviceProviderIds.filter(id => id !== c?.companyId);
      if (additionalCompanies.length < 1) return of([]);
      return combineLatest(additionalCompanies.map(id => this._deviceClient.getDevicesForCompany(id))).pipe(map(d => d.reduce((acc, curr) => acc.concat(curr), [])));
    }), shareReplay(1));

    this.companyContextDeviceMaps$ = combineLatest([this.companyContext$, this.job$]).pipe(switchMap(([c, j]) => {
      //console.log("Company Context:", c, "Job:", j);
      if (!c?.companyId) {
        return of([]);
      }
      const startTime = j.startTime ?? new Date();
      const endTime = j.endTime ?? new Date(2150, 1);
      return this._deviceClient.getDeviceJobMappingForCompany(c.companyId, startTime, endTime);
    }), shareReplay(1));


    //remove serviceProviderIds that are no longer in the vendorCompanies list


  }

  private redirectToConsumerJobIfNeeded(jobId: string, originalError: any): Observable<VendorJob> {
    const serverRedirectPath = this.tryGetRedirectPathFromError(originalError);
    if (serverRedirectPath) {
      this.trackVendorJobRouteMismatch(jobId, {
        reason: 'server-signaled',
        redirectPath: serverRedirectPath,
        httpStatus: String(originalError?.status ?? ''),
      });
      this._router.navigateByUrl(serverRedirectPath, { replaceUrl: true });
      return EMPTY;
    }

    // If the user navigated to /vendorjob/:id with a consumer-job id, send them to /consumerjob/:id.
    return this._consumerJobClient.getConsumerJobById(jobId).pipe(
      tap(() => {
        this.trackVendorJobRouteMismatch(jobId, {
          reason: 'client-detected',
          httpStatus: String(originalError?.status ?? ''),
        });
        this._router.navigate(['/consumerjob', encodeURIComponent(jobId)], { replaceUrl: true });
      }),
      switchMap(() => EMPTY),
      catchError(() => throwError(() => originalError))
    );
  }

  private tryGetRedirectPathFromError(err: any): string | null {
    try {
      if (!err) return null;
      if (err.status !== 409) return null;

      // NSwag errors typically expose a JSON string on err.response.
      const responseText = err.response;
      if (!responseText || typeof responseText !== 'string') return null;

      const parsed = JSON.parse(responseText);
      if (parsed?.code !== 'WrongJobType') return null;
      if (typeof parsed?.redirectPath !== 'string' || parsed.redirectPath.length < 1) return null;
      return parsed.redirectPath;
    } catch {
      return null;
    }
  }

  private trackVendorJobRouteMismatch(jobId: string, extra: { [key: string]: string }) {
    const properties: { [key: string]: string } = {
      jobId,
      currentUrl: this._navigationTrace.currentUrl,
      previousUrl: this._navigationTrace.previousUrl,
      documentReferrer: this._navigationTrace.initialReferrer,
      navigationType: this._navigationTrace.navigationType,
      ...extra,
    };

    this._telemetryService.trackEvent({ name: 'VendorJobRouteMismatch' }, properties);
  }

  //#region *********** Initialization methods ************/


  async republishWorkbook(): Promise<void> {
    const job = await firstValueFrom(this.job$);
    if (!job.id) {
      this._messageService.warnMessage('Cannot republish workbook', 'Job id is missing');
      return;
    }
    try {
      await firstValueFrom(this._jobService.republish(job.id));
      this._messageService.infoMessage('published',
        `published workbook data for - ${job.name}`
      );
    } catch (error) {
      this._messageService.errorMessage(error, 'Could not Republish Workbook Data');
    }
  }

  setPageTitle(jobId: string | undefined) {
    if (!jobId || jobId === 'New' || jobId === '') {
      this._titleService.setTitle('New Job');
    } else { this._titleService.setTitle(jobId); }
  }

  canLinkJob(job: VendorJob): boolean {
    if (this.isNew) return false;
    if (!job) return false;
    if (!job.wellInfo?.name) return false;
    if (!job.apiNumber) return false;
    return true;
  }


  //#endregion

  //#region *********** Getting or Creating new Job methods *******************/
  async createNewJob(): Promise<VendorJob> {
    const companyContext = await firstValueFrom(this.companyContext$);
    const newJob = new VendorJob();
    newJob.id = uuidv4().toUpperCase();
    newJob.startTime = new Date();
    newJob.companyId = companyContext?.companyId ?? '';
    newJob.wellInfo = new WellInfo();
    newJob.wellInfo.casingTubings = new CasingTubings();
    newJob.wellInfo.casingTubings.casing1Vertical = new CasingTubingDetail();
    newJob.wellInfo.casingTubings.casing1Horizontal = new CasingTubingDetail();
    newJob.fluidInfo = new FluidInfo();
    newJob.performanceSummary = new PerformanceSummary();
    newJob.fluidSummary = new FluidSummary();
    return newJob;
  }

  clearEndDate() {
    this.jobToUpdate.endTime = undefined; // Will not be updated in setDates if calendarEndTime is null
  }


  filterNotCompany(items: SelectItem[], companyId: string | undefined) {
    if (!companyId) {
      return items;
    }
    const filtered = items.filter(c => c.value !== companyId);
    //create distinct list of companies by label
    const distinctCompanies = new Map<string, SelectItem>();
    filtered.forEach(item => {
      const label = item.label ?? '';
      if (!distinctCompanies.has(label)) {
        distinctCompanies.set(label, item);
      }
    });

    //sort the distinct companies by label
    filtered.length = 0; // Clear the original array
    distinctCompanies.forEach(item => filtered.push(item));
    const orderByCompanyName = filtered.sort((a, b) => (a.label ?? '').toLowerCase().localeCompare((b.label ?? '').toLowerCase()));
    return orderByCompanyName;
  }


  closeInvalidModelDialog() {
    this.showModelInvalidDialog = false;
  }

  async navigateToDashboard(): Promise<void> {
    const job = await firstValueFrom(this.job$);
    if (!job.id) {
      return;
    }
    const link = await firstValueFrom(this._jobService.getDashboardLink(job.id));
    window.open(link, "_blank");
  }

  async navigateToWorkbook(): Promise<void> {
    const job = await firstValueFrom(this.job$);
    if (job.useOldWorkbookInReadOnlyMode) {
      this._router.navigate(['/workbook/' + this.jobToUpdate.id]);
    } else {
      this._router.navigate(['/workbook2/' + this.jobToUpdate.id]);
    }
  }

  async downloadJobExport(): Promise<void> {
    const job = await firstValueFrom(this.job$);
    if (!job.id) {
      return;
    }
    const securityToken = await firstValueFrom(this.securityToken$);
    window.open('/api/vendorjob/export/' + job.id + '?token=' + securityToken, '_blank');
  }

  async downloadJobSummaryExport(): Promise<void> {
    const job = await firstValueFrom(this.job$);
    if (!job.id) {
      return;
    }
    const securityToken = await firstValueFrom(this.securityToken$);
    window.open('/api/vendorjob/exportsummary/' + job.id + '?token=' + securityToken, '_blank');
  }

  async downloadSandReport() {
    const securityToken = await firstValueFrom(this.securityToken$);
    const beginTime = moment(this.jobToUpdate.startTime).format('YYYY-MM-DDTHH:mm:ss');
    const endTime = moment(this.jobToUpdate.startTime).add(1, 'y').format('YYYY-MM-DDTHH:mm:ss');
    window.open('/api/reports/sand/' +
      this.jobToUpdate.id +
      '?beginTime=' + beginTime +
      '&endTime=' + endTime +
      '&token=' + securityToken, '_blank');
  }

  async downloadDataSheetReport() {
    const securityToken = await firstValueFrom(this.securityToken$);
    const beginTime = moment(this.jobToUpdate.startTime).format('YYYY-MM-DDTHH:mm:ss');
    const endTime = moment(this.jobToUpdate.startTime).add(1, 'y').format('YYYY-MM-DDTHH:mm:ss');
    window.open('/api/reports/datasheet/' +
      this.jobToUpdate.id +
      '?beginTime=' + beginTime +
      '&endTime=' + endTime +
      '&token=' + securityToken, '_blank');
  }

  async saveJob(): Promise<void> {
    const isModelValid = await this.validateJobModel();
    this.showModelInvalidDialog = this.modelInvalid = !isModelValid;

    if (!this.showModelInvalidDialog) {
      this._applicationContextService.showApplicationSpinner(false);
      if (this.isNew) {
        try {
          const result = await firstValueFrom(this._vendorJobClient.createVendorJob(new VendorJob(<any>this.jobToUpdate)));

          await this.saveJobConfiguration();
          // In case this is the first consumer job with this consumer company
          // the company list in the user profile needs to update
          this._applicationContextService.alertCompaniesListUpdated();
          this._applicationContextService.hideApplicationSpinner();
        } catch (error) {
          this._applicationContextService.hideApplicationSpinner();
          this._messageService.errorMessageWithObjectDetail(error, 'Could not create job', JSON.stringify(this.jobToUpdate));
          return;
        }

      } else {
        if (!this.jobToUpdate.id) {
          this._applicationContextService.hideApplicationSpinner();
          this._messageService.errorMessage('Could not update job', 'Missing job id');
          return;
        }
        try {
          const result = await firstValueFrom(this._jobService.put(this.jobToUpdate.id, this.jobToUpdate));
          await this.saveJobConfiguration();
          this._applicationContextService.hideApplicationSpinner();
        } catch (error) {
          this._applicationContextService.hideApplicationSpinner();
          this._messageService.errorMessageWithObjectDetail(error, 'Could not update job', JSON.stringify(this.jobToUpdate));
          return;
        }

      }
      this._router.navigate(['/jobs2/']);
    }
  }
  returnToJobList() {
    this._router.navigate(['/jobs2/']);
  }


  showContactInfo() {
    this.showCompanyContactInfoDialog = true;
  }


  //#region *********** Workbook Configuration Methods ********/


  updateWorkbookConfiguration(event: any) {
    this.jobWorkbookConfiguration = event;
  }

  handleErrorsFromConfiguration(event: any) {
    this._messageService.errorMessage(event, 'Error with Job Workbook Configuration');
  }

  async saveJobConfiguration(): Promise<void> {
    if (!this.jobWorkbookConfiguration) {
      return;
    }
    try {
      await firstValueFrom(this._jobWorkbookConfigurationService.addOrUpdateJobWorkbookConfiguration(this.jobWorkbookConfiguration));
    } catch (error) {
      this._messageService.errorMessage(error as unknown, 'Could not save Job Workbook Configuration');
    }
  }


  //#region *********** Other Helper methods ***********/
  isAdmin(user: User, companyId: string | undefined): boolean {
    if (!companyId) {
      return false;
    }
    return this._userAuthorizationService.isUserAdmin(user, companyId, false);
  }

  isEditorOrAdmin(user: User, companyId: string | undefined): boolean {
    if (!companyId) {
      return false;
    }
    return this._userAuthorizationService.isUserEditorOrAdmin(user, companyId, false);
  }



  async validateJobModel(): Promise<boolean> {
    let valid = true;
    this.invalidCompanyId = false;
    if (!this.jobToUpdate.name) { valid = false; }
    if (!this.jobToUpdate.companyId) { valid = false; }
    const vendorCompanies = await firstValueFrom(this.vendorCompanies$);
    if (this.jobToUpdate.companyId && !vendorCompanies.find(v => v.value === this.jobToUpdate.companyId)) {
      this.invalidCompanyId = true;
      valid = false;
    }
    if (!this.jobToUpdate.consumerCompanyId) { valid = false; }
    // TODO: Does at least one device need to be associated?  If so uncomment
    //      Also uncomment HTML validation message
    // if(!this.job.deviceList || this.job.deviceList.length < 1) valid = false;
    if (!this.jobToUpdate.startTime) { valid = false; }
    if (!this.jobToUpdate.apiNumber && !this.jobToUpdate.wellInfo?.name) { valid = false; }
    // if(!this.job.endTime) valid = false;
    if (!this.jobToUpdate.workbookType) { valid = false; }
    if ((this.jobToUpdate.workbookType === WorkbookJobTypes.flowbackWellTesting) && !this.jobToUpdate.wellInfo?.wellType) {
      valid = false;
    }
    if (this.jobToUpdate.enableOfflineWorkbook && !this.jobToUpdate.workbookType) { valid = false; }

    if ((this.jobToUpdate.workbookType === WorkbookJobTypes.flowbackWellTesting) &&
      (!this.jobToUpdate.frcsDayEngineer || !this.jobToUpdate.dayOperators ||
        !this.jobToUpdate.frcsNightEngineer || !this.jobToUpdate.nightOperators)) {
      valid = false;
    }

    if ((!this.jobWorkbookConfiguration) || this.jobWorkbookConfiguration.workbookMeasurementChannelConfigurations.length < 1) {
      valid = false;
      this._messageService.errorMessageWithObjectDetail("invalid workbook configuration", 'Could not update job', JSON.stringify(this.jobToUpdate));
    }
    if (this.jobToUpdate.fluidInfo?.density != Number(this.jobToUpdate.fluidInfo?.density)) {
      this._messageService.errorMessageWithObjectDetail("invalid fluild density", 'Could not update job', JSON.stringify(this.jobToUpdate));
      valid = false;
    }

    if (this.jobToUpdate.wellInfo && this.jobToUpdate.wellInfo.kop && this.jobToUpdate.wellInfo.kop != Number(this.jobToUpdate.wellInfo.kop)) {
      this._messageService.errorMessageWithObjectDetail("invalid kop", 'Could not update job', JSON.stringify(this.jobToUpdate));
      valid = false;
    }

    if (this.jobToUpdate.wellInfo && this.jobToUpdate.wellInfo.sicp && this.jobToUpdate.wellInfo.sicp != Number(this.jobToUpdate.wellInfo.sicp)) {
      this._messageService.errorMessageWithObjectDetail("invalid scip", 'Could not update job', JSON.stringify(this.jobToUpdate));
      valid = false;
    }
    return valid;
  }

  getMappedDeviceList(job: VendorJob): Observable<Device[]> {
    return combineLatest([this.devices$, this.usersCompanyDevices$, this.additionalServiceProvidersDevices$]).pipe(map(([jobCompany, user, additional]) => {
      if (!job.deviceList || job.deviceList.length < 1) return [];
      const all = jobCompany.concat(user).concat(additional);
      //console.log('alldevices', all);
      const results = [];
      for (const id of job.deviceList) {
        const device = all.find(d => d.id === id);
        if (device) {
          results.push(device);
        } else {
          console.log('device not found', id);
        }
      }
      return results;
    }));
  }

  // Update addDevice method to take deviceId parameter
  addDevice(deviceId: string) {
    if (!this.jobToUpdate.deviceList) {
      this.jobToUpdate.deviceList = [deviceId];
    } else if (!this.jobToUpdate.deviceList?.includes(deviceId)) {
      // Create a new array reference to trigger change detection
      this.jobToUpdate.deviceList = [...this.jobToUpdate.deviceList, deviceId];
    }
    this.showDeviceDialog = false; // Close dialog after assigning
  }

  removeDevice(deviceId: string) {
    this.jobToUpdate.deviceList = (this.jobToUpdate.deviceList ?? []).filter(id => id !== deviceId);
  }

  // Update filter method
  filterDevices(devices: DeviceJobMapping[]): DeviceJobMapping[] {
    if (!this.showOnlyUnassigned) {
      return devices;
    }
    return devices.filter(d => !d.jobId);
  }

  async navigateToCsvImport(): Promise<void> {
    const job = await firstValueFrom(this.job$);
    this._router.navigate(['/csvimport', job.id]);
  }

  getVendorCompanyLabel(vendorCompanies: SelectItem[]): string {
    const found = vendorCompanies.find(vc => vc.value === this.jobToUpdate?.companyId);
    return found?.label || 'Unknown';
  }

  private createDefaultJob(): VendorJob {
    const job = new VendorJob();
    job.wellInfo = new WellInfo();
    job.wellInfo.casingTubings = new CasingTubings();
    job.wellInfo.casingTubings.casing1Vertical = new CasingTubingDetail();
    job.wellInfo.casingTubings.casing1Horizontal = new CasingTubingDetail();
    job.fluidInfo = new FluidInfo();
    job.performanceSummary = new PerformanceSummary();
    job.fluidSummary = new FluidSummary();
    job.deviceList = [];
    job.serviceProviderIds = [];
    return job;
  }

  private normalizeJob(job: VendorJob): VendorJob {
    if (!job.wellInfo) {
      job.wellInfo = new WellInfo();
    }
    if (!job.wellInfo.casingTubings) {
      job.wellInfo.casingTubings = new CasingTubings();
    }
    if (!job.wellInfo.casingTubings.casing1Vertical) {
      job.wellInfo.casingTubings.casing1Vertical = new CasingTubingDetail();
    }
    if (!job.wellInfo.casingTubings.casing1Horizontal) {
      job.wellInfo.casingTubings.casing1Horizontal = new CasingTubingDetail();
    }
    if (!job.fluidInfo) {
      job.fluidInfo = new FluidInfo();
    }
    if (!job.performanceSummary) {
      job.performanceSummary = new PerformanceSummary();
    }
    if (!job.fluidSummary) {
      job.fluidSummary = new FluidSummary();
    }
    if (!job.deviceList) {
      job.deviceList = [];
    }
    if (!job.serviceProviderIds) {
      job.serviceProviderIds = [];
    }
    return job;
  }

  //#endregion
}
